import { useState, useCallback, useEffect, createContext, useContext } from 'react';
import { cn } from '@repo/utils';
import { Button } from './Button';
import { useToastTimer, type Toast, type ToastType, TOAST_SEVERITY_MAP } from '@repo/state';

interface ToastContextValue {
  toasts: Toast[];
  toast: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
  exitingIds: Set<string>;
  markExiting: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const EXIT_ANIMATION_MS = 180;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  const toast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2);

    setToasts((prev) => {
      const updated = [{ ...t, id }, ...prev];
      return updated.slice(0, 3);
    });
  }, []);

  const markExiting = useCallback((id: string) => {
    setExitingIds((prev) => new Set(prev).add(id));
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    setExitingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setExitingIds((prev) => {
      const next = new Set(prev);
      toasts.forEach((t) => next.add(t.id));
      return next;
    });
    setTimeout(() => {
      setToasts([]);
      setExitingIds(new Set());
    }, EXIT_ANIMATION_MS);
  }, [toasts]);

  useEffect(() => {
    const handleGlobalToast = (e: Event) => {
      const customEvent = e as CustomEvent<Omit<Toast, 'id'>>;
      if (customEvent.detail) {
        toast(customEvent.detail);
      }
    };
    window.addEventListener('app:toast', handleGlobalToast);
    return () => window.removeEventListener('app:toast', handleGlobalToast);
  }, [toast]);

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss, clearAll, exitingIds, markExiting }}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  );
}

export function emitToast(t: Omit<Toast, 'id'>) {
  window.dispatchEvent(new CustomEvent('app:toast', { detail: t }));
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const toastBorderColors: Record<ToastType, string> = {
  success: 'border-l-success-500',
  error: 'border-l-danger-500',
  warning: 'border-l-warning-500',
  info: 'border-l-info-500',
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { exitingIds, markExiting } = useContext(ToastContext)!;
  const isExiting = exitingIds.has(toast.id);

  const handleDismiss = () => {
    markExiting(toast.id);
    setTimeout(() => onDismiss(toast.id), EXIT_ANIMATION_MS);
  };

  const { progress, pause, resume } = useToastTimer({
    id: toast.id,
    duration: toast.critical ? Infinity : toast.duration ?? 4000,
    onDismiss: handleDismiss,
  });

  const severityVar = TOAST_SEVERITY_MAP[toast.type];

  return (
    <div
      className={cn(
        'bg-surface-1 border border-border rounded-[10px] border-l-4 shadow-md overflow-hidden',
        toastBorderColors[toast.type],
        isExiting ? 'animate-slide-out-to-right' : 'animate-slide-in-from-right',
      )}
      onMouseEnter={pause}
      onMouseLeave={resume}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">{toast.title}</p>
          {toast.description && (
            <p className="text-xs text-text-secondary mt-0.5">{toast.description}</p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={handleDismiss} aria-label="Dismiss">
          ×
        </Button>
      </div>

      {!toast.critical && (
        <div className="h-[var(--toast-progress-height)] bg-border/30" style={{ transition: 'none' }}>
          <div
            className="h-full rounded-br-[10px]"
            style={{
              width: `${progress * 100}%`,
              backgroundColor: `var(--${severityVar}-500)`,
              transition: 'none',
            }}
          />
        </div>
      )}
    </div>
  );
}

function Toaster() {
  const { toasts, dismiss, clearAll } = useContext(ToastContext)!;

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed z-toast flex flex-col gap-2 w-80 right-4 lg:right-6 max-sm:left-4 max-sm:w-auto"
      style={{ top: '72px' }}
      aria-live="polite"
      role="region"
      aria-label="Notifications"
    >
      {toasts.length > 1 && (
        <div className="flex justify-end max-sm:justify-center mb-1.5">
          <Button variant="secondary" size="sm" onClick={clearAll}>
            Clear All
          </Button>
        </div>
      )}
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}